import { Link } from "react-router-dom";
import { useRef } from "react";
import { ArrowRight, ChevronRight, Users, MapPin, Plane, BarChart2, Shield, Youtube, Play, CheckCircle, Zap, Brain } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import "../../marketing.css";

const YT_CHANNEL = "https://www.youtube.com/channel/UCoaeo2zs2N6zx3UUyEzv08Q";

const PRODUCT_AREAS = [
  {
    icon: Users,
    label: "Crew Rostering",
    badge: "FDTL COMPLIANT",
    tagline: "Intelligent rostering with bio-mathematical fatigue scoring and DGCA FDTL validation built into every pairing.",
    modules: ["Crew Master", "Roster Planner", "Pairing Builder", "Monthly Scheduler", "Compliance Board", "Recovery Console"],
    color: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", dot: "bg-blue-400" },
    href: "/crew-rostering",
  },
  {
    icon: MapPin,
    label: "Ground Operations",
    badge: "GROUNDSHIELD",
    tagline: "Real-time turnaround control, ramp app for field teams, AI delay prediction and vendor SLA accountability.",
    modules: ["Turnaround Control Tower", "Ramp Mobile App", "AI Delay Prediction", "Vendor SLA Manager", "Ground Staff Planning", "Ground Fatigue Shield"],
    color: { bg: "bg-orange-500/10", border: "border-orange-500/20", text: "text-orange-400", dot: "bg-orange-400" },
    href: "/ground-ops",
  },
  {
    icon: Plane,
    label: "Flight Operations",
    badge: "OCC MODULE",
    tagline: "Live flight ops board, IRROPS recovery, OPS map and disruption management for your operations control centre.",
    modules: ["Flight Ops Board", "OPS Map", "IRROPS Console", "Disruption Management", "Crew Deployment", "Swap Requests"],
    color: { bg: "bg-sky-500/10", border: "border-sky-500/20", text: "text-sky-400", dot: "bg-sky-400" },
    href: "/flight-ops",
  },
  {
    icon: BarChart2,
    label: "Analytics",
    badge: "INTELLIGENCE",
    tagline: "Enterprise command dashboard, fatigue trend heatmaps, safety risk scoring and station performance reports.",
    modules: ["Enterprise Dashboard", "Safety Analytics", "Station Performance", "Fatigue Risk Trends", "Back Office Reports", "Vendor SLA Analytics"],
    color: { bg: "bg-violet-500/10", border: "border-violet-500/20", text: "text-violet-400", dot: "bg-violet-400" },
    href: "/analytics",
  },
  {
    icon: Shield,
    label: "Compliance & FRMS",
    badge: "DGCA PHASE 1-3",
    tagline: "Three-phase FRMS journey from FDTL monitoring to predictive bio-mathematical risk to full regulatory FRMS.",
    modules: ["FRMS Phase 1 — FDTL Monitor", "FRMS Phase 2 — Predictive", "FRMS Phase 3 — Full FRMS", "CAMO Planning", "Engineering Risk", "Safety SMS"],
    color: { bg: "bg-green-500/10", border: "border-green-500/20", text: "text-green-400", dot: "bg-green-400" },
    href: "/compliance",
  },
];

const PLATFORM_STATS = [
  { value: "12+", label: "Integrated Modules" },
  { value: "3", label: "FRMS Phases" },
  { value: "100%", label: "DGCA Compliant" },
  { value: "5", label: "Core Domains" },
];

export default function FatigueShieldHome() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-20 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 60% at 50% 20%, rgba(59,130,246,0.08) 0%, rgba(249,115,22,0.04) 60%, transparent 100%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 live-dot" />
              <span className="text-[10px] text-blue-400 marketing-font-mono uppercase font-bold tracking-widest">Fatigue Shield Platform</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-black marketing-font-heading leading-none mb-5">
              Predict fatigue.<br />
              <span className="text-orange-400">Protect every</span><br />
              flight operation.
            </h1>
            <p className="text-lg text-slate-400 max-w-xl mx-auto mb-10">
              Sovereign-built and fully customised for DGCA-regulated airlines — crew fatigue management, ground operations control, flight ops and compliance in one integrated platform.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/marketing/demo"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/20"
                data-testid="hero-demo-btn">
                Request a Demo <ArrowRight size={16} />
              </Link>
              <Link to="/login"
                className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 hover:text-white px-7 py-3.5 transition-all">
                Sign In <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {PLATFORM_STATS.map((s, i) => (
              <div key={i} className="bg-[#0c1929]/60 border border-white/5 p-4 text-center">
                <div className="text-3xl font-black text-white marketing-font-mono mb-1">{s.value}</div>
                <div className="text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5 PRODUCT AREAS ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">5 domains. One platform.</div>
            <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading">Every part of your airline. Connected.</h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-4 mb-10">
            {PRODUCT_AREAS.map((area, i) => {
              const Icon = area.icon;
              return (
                <Link key={i} to={area.href}
                  className={`group bg-[#0c1929]/50 border ${area.color.border} hover:border-opacity-60 p-5 card-glow transition-all fade-in-up`}
                  data-testid={`area-card-${area.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className={`w-9 h-9 ${area.color.bg} border ${area.color.border} flex items-center justify-center mb-4`}>
                    <Icon size={16} className={area.color.text} />
                  </div>
                  <div className={`text-[9px] marketing-font-mono uppercase tracking-widest font-bold mb-2 ${area.color.text}`}>{area.badge}</div>
                  <div className="font-bold text-white text-sm marketing-font-heading mb-3 group-hover:text-orange-100 transition-colors">{area.label}</div>
                  <div className="space-y-1.5 mb-4">
                    {area.modules.slice(0, 4).map((m, j) => (
                      <div key={j} className="flex items-center gap-1.5">
                        <div className={`w-1 h-1 rounded-full ${area.color.dot} shrink-0`} />
                        <span className="text-[10px] text-slate-500">{m}</span>
                      </div>
                    ))}
                    {area.modules.length > 4 && (
                      <div className="text-[10px] text-slate-600 pl-2.5">+{area.modules.length - 4} more</div>
                    )}
                  </div>
                  <div className={`text-[10px] marketing-font-mono font-bold ${area.color.text} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                    Explore <ChevronRight size={10} />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Full taglines row */}
          <div className="grid lg:grid-cols-5 gap-4">
            {PRODUCT_AREAS.map((area, i) => (
              <p key={i} className="text-xs text-slate-600 leading-relaxed">{area.tagline}</p>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW THE DATA CONNECTS ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f] grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">One data layer</div>
            <h2 className="text-3xl font-black marketing-font-heading">Fatigue data flows across every module.</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { icon: Brain, title: "Bio-mathematical fatigue model", desc: "Crew duty patterns, rest periods and circadian rhythm data feed a fatigue score that appears everywhere — pairing validation, roster approval, and the FRMS dashboard.", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
              { icon: Zap, title: "AI-powered disruption response", desc: "When a flight delays, the AI engine scores impact on crew fatigue, suggests recovery crew and pre-calculates FDTL headroom — all before the controller calls the crew.", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
              { icon: Shield, title: "Compliance enforced at the source", desc: "DGCA FDTL 2020 rules are validated during pairing build — not after. Violations are impossible to schedule, not just flagged after the fact.", color: "text-green-400 bg-green-500/10 border-green-500/20" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className={`bg-[#0c1929]/40 border ${item.color.split(" ").find(c => c.startsWith("border"))} p-6`}>
                  <div className={`w-10 h-10 ${item.color.split(" ").find(c => c.startsWith("bg"))} border ${item.color.split(" ").find(c => c.startsWith("border"))} flex items-center justify-center mb-4`}>
                    <Icon size={18} className={item.color.split(" ")[0]} />
                  </div>
                  <div className="font-bold text-white marketing-font-heading mb-3">{item.title}</div>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── YOUTUBE ── */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row items-center gap-8 bg-[#0c1929]/40 border border-red-500/10 p-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded mb-4">
                <Youtube size={12} className="text-red-400" />
                <span className="text-[10px] text-red-400 marketing-font-mono uppercase font-bold tracking-widest">Platform Demos on YouTube</span>
              </div>
              <h3 className="text-2xl font-black marketing-font-heading mb-3">Watch every module in action.</h3>
              <p className="text-sm text-slate-400 mb-5">Walkthroughs, feature deep-dives and live platform demos — all on our YouTube channel. No sales call required.</p>
              <a href={YT_CHANNEL} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold px-6 py-3 transition-all hover:-translate-y-0.5"
                data-testid="platform-yt-btn">
                <Youtube size={15} /> Watch on YouTube
              </a>
            </div>
            <div className="flex gap-3 shrink-0">
              {["Crew FDTL Demo", "Turnaround Live", "AI Delay Score", "FRMS Phase 2"].map((v, i) => (
                <a key={i} href={YT_CHANNEL} target="_blank" rel="noreferrer"
                  className="group w-24 bg-[#07111f] border border-white/8 hover:border-red-500/30 p-3 text-center transition-all">
                  <div className="w-8 h-8 bg-red-600/20 border border-red-500/20 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-red-600/40 transition-colors">
                    <Play size={12} className="text-red-400 ml-0.5" fill="currentColor" />
                  </div>
                  <div className="text-[9px] text-slate-600 leading-tight">{v}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading mb-4">Built for your airline.<br /><span className="text-orange-400">Ready to deploy.</span></h2>
          <p className="text-slate-400 max-w-md mx-auto mb-8">30-minute live demo with demo data configured for your fleet size and base structure.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/marketing/demo"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 text-base transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
              Request Demo <ArrowRight size={17} />
            </Link>
            <a href={YT_CHANNEL} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-400 font-bold px-9 py-4 text-base transition-all">
              <Youtube size={17} /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
